import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flag, Pencil, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import ReuseableDrawer from "../../../components/resuable-drawer";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { formatCurrency } from "../../../utils/function";
import { TeamDetails } from "../../../components/crm/customer/types";
import { ActionsTab, InsightDataTab, useConversationInsights, useScheduledCalls } from "../../../components/crm/conversation/components/insight";
import type { Conversation } from "../../crm-pages/conversation/types";
import { DealBoardItem, DealPerson, DealStage, formatSource } from "./types";
import StageMenu from "./stage-menu";
import EditDealModal from "./edit-deal-modal";
import { goToDealConversation } from "./conversation-nav";

// "Details" is real fields only — GET /deals/{id} per
// docs/deals-ui-integration.md. "Actions" and "Insight" now share the exact
// same real endpoints/components the Leads "View Lead Details" drawer uses
// (components/crm/conversation/components/insight.tsx) — GET
// /conversations/{deal.conversation_id}/insights, /calls, /notes — instead
// of hardcoded mock content. A deal with no linked conversation yet just
// shows the same empty states Leads shows for a lead with no conversation.

type Tab = "actions" | "insight" | "details";

interface ChatMessagePreview {
  id: string;
  direction: "inbound" | "outbound" | string;
  body: string;
  created_at: string;
}

const formatDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const formatBubbleTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "");
};

const formatDatePill = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Today";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const initials = (name?: string | null) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

// GET /deals/{id} returns the raw deal record without the enriched
// `person` sub-object — only board/list items carry that. Merge the fetch
// on top of whatever `person` we already know (the board card that opened
// this drawer) instead of letting it clobber it to undefined.
const FALLBACK_PERSON: DealPerson = {
  type: "contact",
  id: "",
  display_name: "Unknown",
  first_name: null,
  last_name: null,
  phone: null,
  email: null,
  source: null,
  location: null,
  avatar_url: null,
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
    <span className="text-sm text-gray-800 dark:text-gray-100 text-right truncate">{children}</span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-gray-100 dark:border-gray-800 py-4">
      <button type="button" onClick={() => setOpen((s) => !s)} className="flex items-center justify-between w-full">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {open ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

const TabBar: React.FC<{ active: Tab; onChange: (t: Tab) => void }> = ({ active, onChange }) => (
  <div className="flex items-center gap-1 px-5 pt-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
    {(
      [
        ["actions", "Actions"],
        ["insight", "Insight"],
        ["details", "Details"],
      ] as [Tab, string][]
    ).map(([key, label]) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
          active === key
            ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
            : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

// ── Conversation pane (right side, real messages) ───────────────────────────

const ConversationPane: React.FC<{
  loading: boolean;
  conversation: Conversation | null;
  messages: ChatMessagePreview[];
  onViewConversation: () => void;
}> = ({ loading, conversation, messages, onViewConversation }) => (
  <div className="flex flex-col h-full">
    <div className="flex-1 overflow-y-auto space-y-1.5">
      {loading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Looking up conversation…</p>
      ) : !conversation ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No conversation linked to this deal yet.</p>
      ) : messages.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No messages in this conversation yet.</p>
      ) : (
        messages.map((msg, i) => {
          const isInbound = msg.direction === "inbound";
          const prevDate = i > 0 ? formatDatePill(messages[i - 1].created_at) : null;
          const datePill = formatDatePill(msg.created_at);
          return (
            <React.Fragment key={msg.id || i}>
              {datePill !== prevDate && (
                <div className="flex justify-center py-2">
                  <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-200/70 dark:bg-gray-800 rounded-full px-3 py-1">
                    {datePill}
                  </span>
                </div>
              )}
              <div className={`flex ${isInbound ? "justify-start" : "justify-end"}`}>
                <div
                  className={`max-w-[80%] px-4 py-2.5 shadow-sm text-sm rounded-2xl ${
                    isInbound
                      ? "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-tl-none"
                      : "bg-emerald-100 dark:bg-emerald-500/20 text-gray-800 dark:text-gray-200 rounded-tr-none"
                  }`}
                >
                  <p className="whitespace-pre-line break-words">{msg.body || "—"}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 text-right mt-1">{formatBubbleTime(msg.created_at)}</p>
                </div>
              </div>
            </React.Fragment>
          );
        })
      )}
    </div>
    <button
      type="button"
      onClick={onViewConversation}
      className="text-sm font-medium text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-700 dark:hover:text-indigo-300 mt-4 text-left shrink-0"
    >
      View Conversation
    </button>
  </div>
);

// ── Root drawer ──────────────────────────────────────────────────────────────

const DealDetailsDrawer: React.FC<{
  dealId: string | null;
  fallback: DealBoardItem | null;
  // Dashboard response's value_currency — fallback only when the deal has
  // no currency of its own.
  valueCurrency?: string;
  onClose: () => void;
  onStageChanged: (dealId: string, stage: DealStage) => void;
  onUpdated: () => void;
}> = ({ dealId, fallback, valueCurrency, onClose, onStageChanged, onUpdated }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const [deal, setDeal] = useState<DealBoardItem | null>(fallback);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("actions");
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessagePreview[]>([]);
  const [team, setTeam] = useState<TeamDetails[]>([]);

  const { makeRequest: getDeal } = useCrmApi("/deals", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getConversation } = useCrmApi("/conversations", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: createConversation } = useCrmApi("/conversations", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: getTeamList } = useRequest("/profile/users", "GET", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    (async () => {
      const [response] = await getTeamList();
      if (Array.isArray(response)) setTeam(response);
      else if (Array.isArray(response?.users)) setTeam(response.users);
      else if (Array.isArray(response?.results)) setTeam(response.results);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDeal(fallback);
    setTab("actions");
    setConversation(null);
    setMessages([]);
    if (!dealId) return;

    let cancelled = false;
    setLoading(true);
    setConversationLoading(true);
    (async () => {
      const [response, status] = await getDeal(undefined, undefined, dealId);
      if (cancelled) return;
      let resolvedDeal: DealBoardItem | null = fallback;
      if (status >= 200 && status < 300 && response && !response.detail) {
        resolvedDeal = { ...response, person: response.person || fallback?.person };
        setDeal(resolvedDeal);
      }
      setLoading(false);

      const convId = resolvedDeal?.conversation_id;
      if (convId) {
        const [convResponse] = await getConversation(undefined, undefined, convId);
        if (cancelled) return;
        const conv: Conversation | null = convResponse?.id ? convResponse : null;
        setConversation(conv);
        if (conv?.id) {
          const [msgResponse] = await getConversation(undefined, { limit: 10 }, `${conv.id}/messages`);
          if (cancelled) return;
          const list: any[] = Array.isArray(msgResponse)
            ? msgResponse
            : Array.isArray(msgResponse?.data)
            ? msgResponse.data
            : Array.isArray(msgResponse?.messages)
            ? msgResponse.messages
            : [];
          setMessages([...list].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
        }
      }
      setConversationLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const person = deal?.person || FALLBACK_PERSON;

  // A deal with no conversation yet still needs to be schedulable — calls
  // only ever attach to a conversation, so the first schedule attempt
  // creates one on demand (same shape the Leads drawer uses) and the drawer
  // adopts it going forward.
  const ensureConversationForDeal = async (): Promise<string | null> => {
    if (conversation?.id) return conversation.id;
    if (!person.phone) {
      toast.error("This customer has no phone number on file, so a conversation can't be started yet.");
      return null;
    }
    const [response, status] = await createConversation({ title: person.display_name, external_thread_id: person.phone });
    if (status >= 200 && status < 300 && response?.id) {
      setConversation(response);
      return response.id;
    }
    toast.error(response?.detail || response?.message || "Failed to start a conversation for this customer.");
    return null;
  };

  const { insights, loadingInsights } = useConversationInsights(conversation?.id);
  const callsApi = useScheduledCalls(conversation?.id, ensureConversationForDeal);

  const handleAlertOwner = () => {
    if (!deal?.assigned_to_user_id) {
      toast.error("This deal has no assigned owner to alert.");
      return;
    }
    const owner = team.find((m) => m.id === deal.assigned_to_user_id);
    if (!owner) {
      toast.error("This deal has no assigned owner to alert.");
      return;
    }
    toast.success(`${owner.first_name} ${owner.last_name} has been alerted about this deal.`);
  };

  if (!dealId || !deal) return null;

  return (
    <ReuseableDrawer isOpen={!!dealId} onClose={onClose} title="Deal Details" titleIcon={<Flag size={16} />} initialWidth="60%">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between gap-3 px-5 py-3.5 bg-emerald-50/60 dark:bg-emerald-500/10 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            {person.avatar_url ? (
              <img src={person.avatar_url} alt={person.display_name} className="w-9 h-9 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 text-xs font-semibold flex items-center justify-center shrink-0">
                {initials(person.display_name)}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{person.display_name}</p>
              {person.phone && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{person.phone}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {loading && <RefreshCw size={13} className="animate-spin text-gray-400" />}
            <StageMenu dealId={deal.id} stage={deal.stage} onChanged={(s) => { setDeal((d) => (d ? { ...d, stage: s } : d)); onStageChanged(deal.id, s); }} />
          </div>
        </div>

        <TabBar active={tab} onChange={setTab} />

        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          {tab === "actions" ? (
            <>
              <div className="w-full lg:w-[380px] shrink-0 overflow-y-auto px-5 lg:border-r border-gray-100 dark:border-gray-800">
                <ActionsTab
                  insights={insights}
                  loadingInsights={loadingInsights}
                  conversationId={conversation?.id}
                  contactName={person.display_name}
                  team={team}
                  defaultAssignedTo={deal.assigned_to_user_id}
                  callsApi={callsApi}
                  onSendMessage={() => goToDealConversation(navigate, deal.conversation_id, person.phone, person.display_name)}
                  onAlertOwner={handleAlertOwner}
                />
              </div>
              <div className="hidden lg:flex flex-1 overflow-y-auto p-5 flex-col bg-gray-50/60 dark:bg-gray-950/40">
                <ConversationPane
                  loading={conversationLoading}
                  conversation={conversation}
                  messages={messages}
                  onViewConversation={() => goToDealConversation(navigate, deal.conversation_id, person.phone, person.display_name)}
                />
              </div>
            </>
          ) : tab === "insight" ? (
            <>
              <div className="w-full lg:w-[380px] shrink-0 overflow-y-auto px-5 lg:border-r border-gray-100 dark:border-gray-800">
                <InsightDataTab insights={insights} loadingInsights={loadingInsights} />
              </div>
              <div className="hidden lg:flex flex-1 overflow-y-auto p-5 flex-col bg-gray-50/60 dark:bg-gray-950/40">
                <ConversationPane
                  loading={conversationLoading}
                  conversation={conversation}
                  messages={messages}
                  onViewConversation={() => goToDealConversation(navigate, deal.conversation_id, person.phone, person.display_name)}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto px-5">
              <Section title="Deal Info">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{deal.title}</p>
                  <button
                    type="button"
                    onClick={() => setEditOpen(true)}
                    className="flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline shrink-0"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                </div>
                {deal.description && <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">{deal.description}</p>}
                <div className="divide-y divide-gray-50 dark:divide-gray-800">
                  {deal.product_or_service && <Row label="Product / Service">{deal.product_or_service}</Row>}
                  {deal.amount != null && (deal.currency || valueCurrency) && (
                    <Row label="Deal Potential">{formatCurrency(Number(deal.amount), deal.currency || valueCurrency, "symbol")}</Row>
                  )}
                  <Row label="Expected Close">{formatDateTime(deal.expected_close_at)}</Row>
                  {deal.won_at && <Row label="Won At">{formatDateTime(deal.won_at)}</Row>}
                  {deal.lost_at && <Row label="Lost At">{formatDateTime(deal.lost_at)}</Row>}
                  {deal.loss_reason && <Row label="Loss Reason">{deal.loss_reason}</Row>}
                  <Row label="Date Added">{formatDateTime(deal.created_at)}</Row>
                  <Row label="Last Updated">{formatDateTime(deal.updated_at)}</Row>
                </div>
              </Section>

              <Section title="Customer">
                <div className="divide-y divide-gray-50 dark:divide-gray-800">
                  <Row label="Name">{person.display_name}</Row>
                  <Row label="Type">{person.type === "lead" ? "Lead" : "Contact"}</Row>
                  {person.phone && <Row label="Phone">{person.phone}</Row>}
                  {person.email && <Row label="Email">{person.email}</Row>}
                  {person.source && <Row label="Source">{formatSource(person.source)}</Row>}
                  {person.location && <Row label="Location">{person.location}</Row>}
                </div>
              </Section>

              {deal.transaction && (
                <Section title="Transaction">
                  <div className="divide-y divide-gray-50 dark:divide-gray-800">
                    {Object.entries(deal.transaction).map(([key, value]) => (
                      <Row key={key} label={key.replace(/_/g, " ")}>
                        {String(value)}
                      </Row>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}
        </div>
      </div>

      <EditDealModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        deal={deal}
        onSaved={(updated) => {
          setDeal((d) => (d ? ({ ...d, ...updated } as DealBoardItem) : d));
          onUpdated();
        }}
      />
    </ReuseableDrawer>
  );
};

export default DealDetailsDrawer;
